import React from "react";
import { Link } from "react-router-dom";

export type ComparisonSummary = {
  comparison_id: string;
  passage_count: number;
  raw_lpr_observations: number;
  nonblank_lpr_observations: number;
  time_candidate_count: number;
  ambiguous_count: number;
  no_associated_read_count: number;
  window_seconds?: number;
  expected_lag_seconds?: number;
};
export type ComparisonEvent = {
  event_id: string;
  status: "time_candidate" | "ambiguous" | "no_associated_read";
  observation_id: string | null;
  candidates: { observation_id: string; signed_delta_seconds: number }[];
  reason: string;
  evidence_annotation?: { label: string; basis: string; note: string } | null;
};
export type LprComparison = {
  comparison_id: string;
  summary: ComparisonSummary;
  assumptions: {
    lpr_timezone: string;
    note: string;
    window_seconds: number;
    expected_lag_seconds?: number;
    clock_alignment_verified: boolean;
    export_completeness_verified: boolean;
  };
  observations: {
    id: string;
    observed_at: string;
    plate_number: string | null;
    camera_id: string;
  }[];
  events: ComparisonEvent[];
};
export type ComparisonPackage = {
  id: string;
  title: string;
  display_label?: string;
  comparison_id?: string;
  lpr_comparison_summary?: ComparisonSummary | null;
};

export function fiveSecondEvent(event: ComparisonEvent): ComparisonEvent {
  const candidates = event.candidates.filter(
    (candidate) =>
      Number.isFinite(candidate.signed_delta_seconds) &&
      Math.abs(candidate.signed_delta_seconds) <= 5,
  );
  const paired =
    event.status === "time_candidate" &&
    candidates.some(
      (candidate) => candidate.observation_id === event.observation_id,
    );
  const status = paired
    ? "time_candidate"
    : event.status !== "no_associated_read" && candidates.length
      ? "ambiguous"
      : "no_associated_read";
  return {
    ...event,
    candidates,
    status,
    observation_id: paired ? event.observation_id : null,
    reason:
      status === event.status && candidates.length === event.candidates.length
        ? event.reason
        : "",
  };
}

export function fiveSecondSummary(
  comparison: LprComparison,
): ComparisonSummary {
  const events = comparison.events.map(fiveSecondEvent);
  return {
    ...comparison.summary,
    passage_count: events.length,
    window_seconds: 5,
    expected_lag_seconds: 0,
    time_candidate_count: events.filter(
      (event) => event.status === "time_candidate",
    ).length,
    ambiguous_count: events.filter((event) => event.status === "ambiguous")
      .length,
    no_associated_read_count: events.filter(
      (event) => event.status === "no_associated_read",
    ).length,
  };
}

function hasFiveSecondSummary(summary: ComparisonSummary) {
  return (
    summary.window_seconds !== undefined &&
    summary.window_seconds > 0 &&
    summary.window_seconds <= 5 &&
    summary.expected_lag_seconds === 0
  );
}

export function comparisonLabel(original?: ComparisonEvent) {
  const event = original ? fiveSecondEvent(original) : undefined;
  if (!event) return "Comparison unavailable";
  if (event.evidence_annotation?.label === "No rear plate visible")
    return "No rear plate visible";
  if (event.status === "time_candidate") return "Plate read within 5 seconds";
  if (event.status === "ambiguous") return "Association unclear";
  return "Unpaired within 5 seconds";
}

export function comparisonCohort(
  packages: ComparisonPackage[],
  comparison: LprComparison,
) {
  return packages.filter(
    (item) =>
      item.lpr_comparison_summary &&
      (item.lpr_comparison_summary.comparison_id || item.comparison_id) ===
        comparison.comparison_id,
  );
}

export function ComparisonOverview({
  comparison,
  packages,
  packageId,
  onLink,
}: {
  comparison: LprComparison;
  packages: ComparisonPackage[];
  packageId: string;
  onLink: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}) {
  const cohort = comparisonCohort(packages, comparison);
  const detailSummary = fiveSecondSummary(comparison);
  const summaryFor = (item: ComparisonPackage) =>
    item.id === packageId ? detailSummary : item.lpr_comparison_summary!;
  const summaries = cohort.length ? cohort.map(summaryFor) : [detailSummary];
  const comparisonReady = summaries.every(hasFiveSecondSummary);
  const total = (
    key:
      | "passage_count"
      | "nonblank_lpr_observations"
      | "time_candidate_count"
      | "ambiguous_count"
      | "no_associated_read_count",
  ) => summaries.reduce((sum, item) => sum + item[key], 0);
  return (
    <section
      className="loop-comparison-overview"
      aria-label="Loop and plate reader comparison"
    >
      <div className="loop-section-title">
        <div>
          <p className="loop-eyebrow">LOOP / LPR</p>
          <h2>Vehicle and plate-read comparison</h2>
        </div>
        <span className="loop-chip">Preliminary comparison</span>
      </div>
      <p className="loop-comparison-intro">
        Compare plate reads within five seconds of each passage, and use the
        video to understand the exceptions.
      </p>
      <div className="loop-metrics loop-comparison-metrics">
        {[
          [
            total("passage_count"),
            "Vehicle passages",
            "Counted in the compared recordings",
          ],
          [
            total("nonblank_lpr_observations"),
            "Plate reads",
            "Export rows with plate text; may include repeat reads",
          ],
          [
            comparisonReady ? total("time_candidate_count") : "Pending",
            "Paired within 5 seconds",
            comparisonReady
              ? "Associated by time; vehicle identity still needs review"
              : "Waiting for a five-second comparison for the remaining recordings",
          ],
        ].map(([value, label, detail]) => (
          <div key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
            <p>{detail}</p>
          </div>
        ))}
      </div>
      <p className="loop-comparison-caveat">
        {comparisonReady
          ? `${total("no_associated_read_count")} unpaired · ${total("ambiguous_count")} unclear. Unpaired means no read was assigned within five seconds; unclear means competing associations need review.`
          : "Some recordings are pending a five-second comparison. Counts for the selected recording are checked against its individual events below."}
      </p>
      <p className="loop-comparison-caveat">
        These totals are not a verified capture rate. A nearby read may belong
        to another vehicle; a missing read does not tell us whether a plate was
        readable.
      </p>
      {comparison.events.some(
        (event) => event.evidence_annotation?.label === "No rear plate visible",
      ) && (
        <div className="loop-comparison-highlight">
          <strong>Counted even without a visible rear plate</strong>
          <p>Human observations from this recording:</p>
          {comparison.events
            .filter(
              (event) =>
                event.evidence_annotation?.label === "No rear plate visible",
            )
            .map((event) => (
              <Link
                key={event.event_id}
                to={`/loop/comparison?${new URLSearchParams({ package: packageId, event: event.event_id })}`}
                onClick={onLink}
              >
                No rear plate visible · watch vehicle clip →
              </Link>
            ))}
        </div>
      )}
      {cohort.length > 0 && (
        <div className="loop-comparison-table-wrap">
          <table className="loop-comparison-table">
            <caption>
              Compared recordings · select a lane to inspect its evidence
            </caption>
            <thead>
              <tr>
                <th scope="col">Recording / lane</th>
                <th scope="col">Passages</th>
                <th scope="col">Plate reads</th>
                <th scope="col">Paired within 5 seconds</th>
                <th scope="col">Unpaired</th>
                <th scope="col">Unclear</th>
              </tr>
            </thead>
            <tbody>
              {cohort.map((item) => (
                <tr
                  key={item.id}
                  aria-current={item.id === packageId ? "true" : undefined}
                >
                  <th scope="row">
                    <Link
                      to={`/loop/comparison?${new URLSearchParams({ package: item.id })}`}
                      onClick={onLink}
                    >
                      {item.display_label || item.title}
                    </Link>
                  </th>
                  <td>{summaryFor(item).passage_count}</td>
                  <td>
                    {item.lpr_comparison_summary!.nonblank_lpr_observations}
                  </td>
                  {hasFiveSecondSummary(summaryFor(item)) ? (
                    <>
                      <td>{summaryFor(item).time_candidate_count}</td>
                      <td>{summaryFor(item).no_associated_read_count}</td>
                      <td>{summaryFor(item).ambiguous_count}</td>
                    </>
                  ) : (
                    <td colSpan={3}>Pending five-second comparison</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <details className="loop-comparison-time">
        <summary>Time alignment is assumed · comparison details</summary>
        <p>
          Plate export times are treated as{" "}
          {comparison.assumptions.lpr_timezone === "America/Chicago"
            ? "Omaha local time"
            : comparison.assumptions.lpr_timezone}
          . Camera and plate-reader clocks have not been verified against each
          other. {comparison.assumptions.note}
        </p>
      </details>
    </section>
  );
}

export function EventComparison({
  comparison,
  eventId,
}: {
  comparison?: LprComparison | null;
  eventId: string;
}) {
  const original = comparison?.events.find((item) => item.event_id === eventId);
  const event = original ? fiveSecondEvent(original) : undefined;
  const annotation = event?.evidence_annotation;
  const candidates = [...(event?.candidates || [])].sort(
    (a, b) =>
      Number(b.observation_id === event?.observation_id) -
      Number(a.observation_id === event?.observation_id),
  );
  return (
    <aside className="loop-read-comparison" aria-label="Plate reader evidence">
      <p className="loop-eyebrow">PLATE READER</p>
      <h3>{comparisonLabel(event)}</h3>
      {annotation ? (
        <div className="loop-comparison-observation">
          <strong>Video observation</strong>
          <p>{annotation.note}</p>
          <span>
            {annotation.basis === "user_confirmation"
              ? "Human observation from video"
              : "Recorded evidence observation"}
          </span>
        </div>
      ) : null}
      {!event ? (
        <p>
          No plate-reader comparison is available for this event. This does not
          mean the vehicle had no plate read.
        </p>
      ) : event.status === "time_candidate" ? (
        <p>
          A read occurred within five seconds of this passage. Compare the
          vehicle in the clip before treating it as a match.
        </p>
      ) : event.status === "ambiguous" ? (
        <p>
          Nearby vehicles and reads have competing timing matches. The vehicle
          association needs review.
        </p>
      ) : (
        <p>
          No read has been assigned within five seconds of this passage. This
          alone does not establish a missed readable plate.
        </p>
      )}
      {annotation?.label === "No rear plate visible" && (
        <p>
          The loop still counts this vehicle. This is separate from a visible,
          readable plate that the reader failed to capture.
        </p>
      )}
      {candidates.length > 0 && (
        <div className="loop-read-candidates">
          <h4>
            {event?.status === "time_candidate"
              ? "Provisional plate association"
              : "Reads within five seconds for context"}
          </h4>
          {candidates.map((candidate) => {
            const read = comparison?.observations.find(
              (item) => item.id === candidate.observation_id,
            );
            return (
              <div
                className="loop-read-candidate"
                key={candidate.observation_id}
              >
                <span className="loop-read-candidate-label">
                  {event?.status === "time_candidate" &&
                  event.observation_id === candidate.observation_id
                    ? "Selected read · review evidence below"
                    : "Nearby read · not assigned to this passage"}
                </span>
                <strong>
                  {read?.plate_number || "No plate text in export"}
                </strong>
                {read && (
                  <time>
                    {read.observed_at.replace("T", " ")} · export time
                  </time>
                )}
                <span>
                  {Math.abs(candidate.signed_delta_seconds).toFixed(1)} sec{" "}
                  {candidate.signed_delta_seconds < 0 ? "before" : "after"}{" "}
                  passage
                </span>
                <small>
                  {event?.status === "time_candidate" &&
                  event.observation_id === candidate.observation_id
                    ? "Provisional time match"
                    : "Nearby only · not assigned to this vehicle"}
                </small>
              </div>
            );
          })}
        </div>
      )}
      {event?.reason && <p className="loop-read-reason">{event.reason}</p>}
    </aside>
  );
}
